import { Inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { omit } from 'lodash-es';
import { Observable, of, throwError } from 'rxjs';
import { map } from 'rxjs/operators';

import type { UserToken } from '@plugins/shared/types/auth';

import { HIDE_TOKENS } from './auth.const';
import { AuthService } from './auth.service';

/**
 * Ensure's the user is authenticated based on a url query parameter named 'jwt'.
 * It also features a role permission check. If there's a 'role' attribute in
 * the route's data section, if will match it to the users role, f.e.
 *
 * {  path: '/admin',
 *    component: AdminComponent,
 *    canActivate: [AuthGuard],
 *    data: { role: 'editor' }       // only authenticated 'editor's can enter
 * }
 */
@Injectable()
export class AuthGuard {
  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    @Inject(HIDE_TOKENS) private readonly hidetokens: boolean,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    if (this.authService.isLoggedIn()) {
      return of(this.checkRole(route, this.authService.userToken));
    }
    const { queryParamMap } = route;
    const [jwtParam, handoverParam] = [
      queryParamMap.get('jwt'),
      queryParamMap.get('handover'),
    ];
    if (jwtParam) {
      return this.authService
        .login(jwtParam)
        .pipe(map((user) => this.checkRole(route, user)));
    }
    if (handoverParam) {
      return this.authService.handover(handoverParam).pipe(
        map((user) => this.checkRole(route, user)),
        map((checked) => {
          if (this.hidetokens) {
            const url = this.router.parseUrl(state.url);
            url.queryParams = omit(route.queryParams, 'handover');
            return url;
          }
          return checked;
        }),
      );
    }
    // dev helper: reload emulator
    window.parent.postMessage(
      {
        cmd: 'eep.emulator.reload',
        val:
          window.location.href
            .match(/admin/)?.[0]
            ?.replace('admin', 'editor') ?? '',
      },
      '*',
    );

    return throwError(() => new Error('errors.noJwt'));
  }

  private checkRole(
    route: ActivatedRouteSnapshot,
    user: UserToken,
  ): boolean | UrlTree {
    if (route.data['role']) {
      if (route.data['role'] !== this.authService.userToken.role) {
        window.parent.postMessage(
          {
            cmd: 'eep.emulator.reload',
            val: '',
          },
          '*',
        );
        return this.router.createUrlTree(['/']);
      }
    }
    return true;
  }
}
